
import { FileText, Languages, FolderOpen, Upload, Download, Share2, Search, Lock, History, Zap, CheckCircle, Globe, BookOpen, Baseline } from "lucide-react";
import { FeaturePageLayout } from "@/components/layout/FeaturePageLayout";
import { BenefitCard } from "@/components/ui/benefit-card";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";

const DocumentManagement = () => {
  const { t, i18n } = useTranslation("platform");
  
  const documentBenefits = [
    {
      title: t("documentManagement.benefits.centralizedStorage"),
      description: t("documentManagement.benefits.centralizedStorageDesc"),
      icon: <FolderOpen className="h-5 w-5 text-primary" />
    },
    {
      title: t("documentManagement.benefits.easyCollaboration"),
      description: t("documentManagement.benefits.easyCollaborationDesc"),
      icon: <Share2 className="h-5 w-5 text-primary" />
    },
    {
      title: t("documentManagement.benefits.powerfulSearch"),
      description: t("documentManagement.benefits.powerfulSearchDesc"),
      icon: <Search className="h-5 w-5 text-primary" />
    },
    {
      title: t("documentManagement.benefits.versionControl"),
      description: t("documentManagement.benefits.versionControlDesc"),
      icon: <History className="h-5 w-5 text-primary" />
    }
  ];

  const translationBenefits = [
    {
      title: t("aiTranslation.breakBarriers"),
      description: t("aiTranslation.breakBarriersDescription"),
      icon: <Globe className="h-5 w-5 text-primary" />
    },
    {
      title: t("aiTranslation.technicalPrecision"),
      description: t("aiTranslation.technicalPrecisionDescription"),
      icon: <Baseline className="h-5 w-5 text-primary" />
    },
    {
      title: t("aiTranslation.timeEfficiency"),
      description: t("aiTranslation.timeEfficiencyDescription"),
      icon: <Zap className="h-5 w-5 text-primary" />
    },
    {
      title: t("aiTranslation.broaderAccess"),
      description: t("aiTranslation.broaderAccessDescription"),
      icon: <BookOpen className="h-5 w-5 text-primary" />
    }
  ];

  const documentFeatures = [
    t("documentManagement.features.docTagging"),
    t("documentManagement.features.docExpiration"),
    t("documentManagement.features.docOcr"),
    t("documentManagement.features.docCollab"),
    t("documentManagement.features.docTemplates"),
    t("documentManagement.features.docBulk"),
    t("documentManagement.features.docFolders"),
    t("documentManagement.features.docSharing")
  ];
  
  const languages = [
    "English", "German", "French", "Italian", "Spanish", 
    "Portuguese", "Dutch", "Danish", "Swedish", "Finnish", 
    "Polish", "Czech", "Hungarian", "Romanian", "Bulgarian", 
    "Greek", "Estonian", "Latvian", "Lithuanian", "Slovak", 
    "Slovenian", "Maltese", "Irish", "Croatian"
  ];
  
  // Translate language names if in Danish
  const getLanguageName = (language: string) => {
    if (i18n.language === 'da') {
      const languageMap: Record<string, string> = {
        "English": "Engelsk",
        "German": "Tysk",
        "French": "Fransk",
        "Italian": "Italiensk",
        "Spanish": "Spansk",
        "Portuguese": "Portugisisk",
        "Dutch": "Hollandsk",
        "Danish": "Dansk",
        "Swedish": "Svensk",
        "Finnish": "Finsk",
        "Polish": "Polsk",
        "Czech": "Tjekkisk",
        "Hungarian": "Ungarsk",
        "Romanian": "Rumænsk",
        "Bulgarian": "Bulgarsk",
        "Greek": "Græsk",
        "Estonian": "Estisk",
        "Latvian": "Lettisk",
        "Lithuanian": "Litauisk",
        "Slovak": "Slovakisk",
        "Slovenian": "Slovensk",
        "Maltese": "Maltesisk",
        "Irish": "Irsk",
        "Croatian": "Kroatisk"
      };
      return languageMap[language] || language;
    }
    return language;
  };

  return (
    <FeaturePageLayout
      title={t("documentManagement.combinedTitle") || "Dokumenthåndtering og oversættelser"}
      subtitle={t("documentManagement.subtitle")}
      description={t("documentManagement.combinedDescription") || t("documentManagement.description")}
      icon={<FileText className="h-8 w-8 text-white" />}
      bgColor="bg-gradient-to-r from-teal-500 to-emerald-600"
    >
      <Tabs defaultValue="documents" className="w-full mb-16">
        <TabsList className="mb-8 w-full justify-start">
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span>{t("documentManagement.tabDocuments") || "Dokumenthåndtering"}</span>
          </TabsTrigger>
          <TabsTrigger value="translations" className="flex items-center gap-2">
            <Languages className="h-4 w-4" />
            <span>{t("documentManagement.tabTranslations") || "Oversættelser"}</span>
          </TabsTrigger>
        </TabsList>
        
        {/* Document Management Tab Content */}
        <TabsContent value="documents" className="space-y-6">
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{t("documentManagement.features.simpleTitle")}</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t("documentManagement.features.simpleDescription")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-lg text-center"
              >
                <div className="mb-4 bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t("documentManagement.features.upload")}</h3>
                <p className="text-gray-600 text-sm">
                  {t("documentManagement.features.uploadDesc")}
                </p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-lg text-center"
              >
                <div className="mb-4 bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <Share2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t("documentManagement.features.collaborate")}</h3>
                <p className="text-gray-600 text-sm">
                  {t("documentManagement.features.collaborateDesc")}
                </p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-lg text-center"
              >
                <div className="mb-4 bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <Download className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t("documentManagement.features.access")}</h3>
                <p className="text-gray-600 text-sm">
                  {t("documentManagement.features.accessDesc")}
                </p>
              </motion.div>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8 rounded-lg mb-12"
            >
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="md:w-1/3">
                  <h3 className="text-xl font-semibold mb-4">{t("documentManagement.features.security")}</h3>
                  <div className="flex items-start mb-4">
                    <Lock className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <p className="text-gray-600 text-sm">
                      {t("documentManagement.features.securityDesc")}
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="mr-2 mt-1 text-primary">•</span>
                      {t("documentManagement.features.encryption")}
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 mt-1 text-primary">•</span>
                      {t("documentManagement.features.rbac")}
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 mt-1 text-primary">•</span>
                      {t("documentManagement.features.audit")}
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 mt-1 text-primary">•</span>
                      {t("documentManagement.features.gdpr")}
                    </li>
                  </ul>
                </div>
                <div className="md:w-2/3">
                  <h3 className="text-xl font-semibold mb-4">{t("documentManagement.features.advancedTitle")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {documentFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                        <div className="p-1 mr-3 bg-primary/10 rounded-full">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 13L9 17L19 7" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </section>

          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{t("documentManagement.keyBenefits")}</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t("documentManagement.keyBenefitsDescription")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {documentBenefits.map((benefit, index) => (
                <BenefitCard
                  key={index}
                  title={benefit.title}
                  description={benefit.description}
                  icon={benefit.icon}
                  delay={index * 0.1}
                />
              ))}
            </div>
          </section>
        </TabsContent>
        
        {/* Translation Tab Content */}
        <TabsContent value="translations" className="space-y-6">
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{t("aiTranslation.howWorks")}</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t("aiTranslation.howWorksDescription")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-lg text-center"
              >
                <div className="mb-4 bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <span className="text-2xl">1</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{t("aiTranslation.uploadDocuments")}</h3>
                <p className="text-gray-600 text-sm">
                  {t("aiTranslation.uploadDocumentsDescription")}
                </p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-lg text-center"
              >
                <div className="mb-4 bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <span className="text-2xl">2</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{t("aiTranslation.selectLanguage")}</h3>
                <p className="text-gray-600 text-sm">
                  {t("aiTranslation.selectLanguageDescription")}
                </p>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-lg text-center"
              >
                <div className="mb-4 bg-primary/10 p-4 rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                  <span className="text-2xl">3</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{t("aiTranslation.receiveTranslation")}</h3>
                <p className="text-gray-600 text-sm">
                  {t("aiTranslation.receiveTranslationDescription")}
                </p>
              </motion.div>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg mb-12">
              <h3 className="text-xl font-semibold mb-6">{t("aiTranslation.supportedLanguages")}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {languages.map((language, index) => (
                  <div 
                    key={index} 
                    className="flex items-center p-2 rounded-md bg-white shadow-sm"
                  >
                    <CheckCircle className="h-4 w-4 text-primary mr-2" />
                    <span className="text-sm">{getLanguageName(language)}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{t("aiTranslation.keyBenefits")}</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t("aiTranslation.keyBenefitsDescription")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {translationBenefits.map((benefit, index) => (
                <BenefitCard
                  key={index}
                  title={benefit.title}
                  description={benefit.description}
                  icon={benefit.icon}
                  delay={index * 0.1}
                />
              ))}
            </div>
          </section>

          <section className="mb-20">
            <div className="bg-primary/5 p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">{t("aiTranslation.translationAccuracy")}</h3>
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <h4 className="font-medium mb-2">{t("aiTranslation.technicalTerminology")}</h4>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: "95%" }}></div>
                  </div>
                  <p className="text-right text-sm mt-1">95% {t("aiTranslation.accuracy")}</p>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium mb-2">{t("aiTranslation.legalTerms")}</h4>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: "93%" }}></div>
                  </div>
                  <p className="text-right text-sm mt-1">93% {t("aiTranslation.accuracy")}</p>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium mb-2">{t("aiTranslation.formatPreservation")}</h4>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: "98%" }}></div>
                  </div>
                  <p className="text-right text-sm mt-1">98% {t("aiTranslation.accuracy")}</p>
                </div>
              </div>
            </div>
          </section>
        </TabsContent>
      </Tabs>
    </FeaturePageLayout>
  );
};

export default DocumentManagement;
